import { DeviceIntegrationCard } from '../Styles/DeviceIntegrationStyles';
import { IntegratedServiceStatus, ServiceState } from '@ekkogmbh/apisdk';
import { useMemo } from 'react';
import { Chip, Grid, List, ListItem, Stack, Typography } from '@mui/material';
import { green, red } from '@mui/material/colors';
import { saturation } from 'src/SystemStatus/Styles/SystemStatusStyles';
import { HelpOutlined } from '@mui/icons-material';
import { StatisticElement } from './StatisticElement';
import TooltipHardened from 'src/Common/Components/TooltipHardened';
import { CenteredColumn } from 'src/Common/Components/Stacks';

const keyToLabel = (key: string): string => key.split(/(?=[A-Z])/).join(' ');

const InfoElement = ({ title, info }: { title: string; info: string }) => {
  const prettierTitle = useMemo(() => {
    const splitName = keyToLabel(title);
    return splitName.charAt(0).toUpperCase() + splitName.slice(1);
  }, [title]);

  return (
    <DeviceIntegrationCard title={prettierTitle}>
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 700,
          textAlign: 'center',
          userSelect: 'none',
        }}
      >
        {info}
      </Typography>
    </DeviceIntegrationCard>
  );
};

interface Props {
  serviceStatus: IntegratedServiceStatus;
}

export const ServiceStatusCard = ({ serviceStatus }: Props): React.JSX.Element => {
  const header = useMemo(() => {
    const { deviceDescription, nodePath, status, connectionStatusMessage } = serviceStatus;

    const capabilityNames = deviceDescription.capabilities.split(',');
    const capabilityList = (
      <Stack>
        <Typography>{'Capabilities'}</Typography>
        <List dense>
          {capabilityNames.map((c, i) => (
            <ListItem key={i}>
              <Typography>{c}</Typography>
            </ListItem>
          ))}
        </List>
      </Stack>
    );

    const statusChip = (
      <Chip
        label={status.toUpperCase()}
        sx={{
          color: (theme) => theme.palette.common.white,
          backgroundColor: status === ServiceState.ONLINE ? green[saturation] : red[saturation],
          fontSize: 18,
          fontWeight: 700,
          float: 'right',
          userSelect: 'none',
        }}
      />
    );

    return (
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography color={'textSecondary'}>{nodePath}</Typography>
        <Stack direction={'row'} spacing={0} sx={{ alignItems: 'baseline' }}>
          <Typography fontWeight={700} color={'textPrimary'} fontSize={24}>
            {deviceDescription.name}
          </Typography>
          <TooltipHardened tooltip={capabilityList} arrow>
            <HelpOutlined fontSize={'small'} />
          </TooltipHardened>
        </Stack>
        <TooltipHardened tooltip={connectionStatusMessage} arrow>
          {statusChip}
        </TooltipHardened>
      </Stack>
    );
  }, [serviceStatus]);

  const infoElements = useMemo(
    () =>
      Object.entries(serviceStatus.information).map(([key, value], idx) => (
        <InfoElement key={idx} title={keyToLabel(key)} info={value} />
      )),
    [serviceStatus.information],
  );

  const statisticElements = useMemo(() => {
    const stats = serviceStatus.statistics ?? [];

    return stats.map((s, i) => <StatisticElement key={i} statistic={s} />);
  }, [serviceStatus.statistics]);

  return (
    <CenteredColumn width={'100%'}>
      {header}
      <Grid container spacing={2} sx={{ alignItems: 'stretch', justifyContent: 'center' }}>
        {[...statisticElements, ...infoElements].map((element, i) => (
          <Grid key={i} item xs={2}>
            {element}
          </Grid>
        ))}
      </Grid>
    </CenteredColumn>
  );
};
